// Subscribes to a device's status topic and returns the latest parsed status
// string (the substring between ] and # on `<uno>/client`). Mirrors how the
// Flutter screens listen to mqttMessageStream per device.
//
// -> [status, setStatus, liveAt]
//
// ⚠️ `status` DOES NOT MEAN THE DEVICE IS THERE. It is seeded from the device
// row, so it holds whatever the server last recorded — which may be months old
// — and it is also written by the local DB read some cards do. A card that
// treated a non-empty status as a sign of life painted a green dot on three
// devices that had not reported in eleven days.
//
// `liveAt` is the only honest answer to "have we heard from it": the clock time
// a message actually ARRIVED ON THE WIRE, null until one does. Anything asking
// whether the device is present must use that, never `status`.

import { useEffect, useState } from 'react';
import {
  subscribeTopic,
  unsubscribeTopic,
  addMessageListener,
  parseStatus,
} from './mqttClient';

export function useDeviceStatus(uniqueNumber, initial = '') {
  const [status, setStatus] = useState(initial);
  const [liveAt, setLiveAt] = useState(null);

  useEffect(() => {
    if (!uniqueNumber) return undefined;
    // A different device has been given nothing yet, whatever we heard from the
    // last one.
    setLiveAt(null);
    const topic = `${uniqueNumber}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      setStatus(parseStatus(payload));
      setLiveAt(Date.now());
    });
    return () => {
      off();
      unsubscribeTopic(topic);
    };
  }, [uniqueNumber]);

  return [status, setStatus, liveAt];
}
